import React, { useState } from 'react';
import { Box, IconButton, Typography, CircularProgress } from '@mui/material';
import { Document, Page, pdfjs } from 'react-pdf';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.mjs`;

function ManualViewerPage() {
  const params = new URLSearchParams(window.location.search);
  const pdfUrl = params.get('url') ? decodeURIComponent(params.get('url')) : '';

  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [loadError, setLoadError] = useState(false);
  const [containerWidth, setContainerWidth] = useState(window.innerWidth);

  React.useEffect(() => {
    const handleResize = () => setContainerWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (!pdfUrl) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          backgroundColor: '#f5f5f5',
        }}
      >
        <Typography color="error">매뉴얼 URL이 올바르지 않습니다.</Typography>
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          backgroundColor: '#f5f5f5',
        }}
      >
        <Typography color="error">매뉴얼 PDF를 불러올 수 없습니다.</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        backgroundColor: '#f5f5f5',
      }}
    >
      {/* 헤더 */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 3,
          py: 1.5,
          borderBottom: '1px solid #e0e0e0',
          backgroundColor: '#fff',
          flexShrink: 0,
        }}
      >
        <Typography fontWeight={600} fontSize="1.1rem">
          관리자 매뉴얼
        </Typography>
      </Box>

      {/* PDF 뷰어 */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          py: 2,
        }}
      >
        <Document
          file={pdfUrl}
          onLoadSuccess={({ numPages: n }) => setNumPages(n)}
          onLoadError={() => setLoadError(true)}
          loading={
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '60vh',
                gap: 2,
              }}
            >
              <CircularProgress size={24} />
              <Typography>불러오는 중...</Typography>
            </Box>
          }
        >
          <Page
            pageNumber={pageNumber}
            width={Math.min(containerWidth * 1.0, 1600)}
            renderAnnotationLayer={false}
            renderTextLayer={false}
          />
        </Document>
      </Box>

      {/* 페이지 네비게이션 */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          py: 1,
          borderTop: '1px solid #e0e0e0',
          backgroundColor: '#fff',
          flexShrink: 0,
        }}
      >
        <IconButton
          onClick={() => setPageNumber((p) => Math.max(p - 1, 1))}
          disabled={pageNumber <= 1}
        >
          <NavigateBeforeIcon />
        </IconButton>
        <Typography fontSize="0.95rem">
          {pageNumber} / {numPages ?? '...'}
        </Typography>
        <IconButton
          onClick={() => setPageNumber((p) => Math.min(p + 1, numPages ?? p))}
          disabled={pageNumber >= (numPages ?? 1)}
        >
          <NavigateNextIcon />
        </IconButton>
      </Box>
    </Box>
  );
}

export default ManualViewerPage;
